import { cn } from "@/utils/utils";
import {
  AdminReportGraphMetricsQueryVariables,
  useAdminReportGraphMetricsQuery,
} from "@common/utils/graphql";
import { Body, Spinner, Superscript } from "@hypercard/web-components";
import { useMemo } from "react";
import { renderGroupBy } from "./renderGroupBy";
import { renderAmount } from "./renderAmount";
import { formatDollars } from "@common/components/utils";
import { getNameForExpenseReportAnalyticGroup } from "@domains/stats/utils";
import { Separator } from "@/components/ui/separator";
import { ChartBar } from "@phosphor-icons/react";
import {
  StatsTab,
  useCurrentStatsTab,
} from "@domains/stats/utils/useCurrentStatsTab";
import { useReportsTabInputs } from "@domains/stats/ReportsTab/useReportsTabInputs";
import { useHomeTabInputs } from "@domains/stats/HomeTab/useHomeTabInputs";
import { filtersToFilterOptions } from "./filtersToFilterOptions";

type Props = {
  headerText: string;
  variables: AdminReportGraphMetricsQueryVariables;
  className?: string;
};

export const TopListChart = ({ headerText, variables, className }: Props) => {
  const {
    data: rawData,
    loading,
    error: graphqlError,
  } = useAdminReportGraphMetricsQuery({
    variables,
  });

  const data = useMemo(() => {
    if (
      rawData?.adminReportGraphMetrics.__typename !==
      "QueryAdminReportGraphMetricsSuccess"
    )
      return [];

    return rawData.adminReportGraphMetrics.data.metrics
      .map((metric) => {
        return {
          groupBy: renderGroupBy(variables.groupedBy, metric.groupedEntityName),
          amount: renderAmount(
            variables.groupedBy,
            formatDollars(metric.totalValue),
          ),
        };
      })
      .slice(0, 10);
  }, [rawData, variables.groupedBy]);

  const error = useMemo(
    () =>
      !!graphqlError ||
      rawData?.adminReportGraphMetrics.__typename?.includes("Error"),
    [graphqlError, rawData?.adminReportGraphMetrics.__typename],
  );

  const { setTab } = useCurrentStatsTab();
  const { upsert } = useReportsTabInputs();
  const { dateRange } = useHomeTabInputs();

  const onClickOpenChart = () => {
    upsert({
      dateRange,
      groupedBy: variables.groupedBy,
      filterOptions: variables.filters
        ? filtersToFilterOptions(variables.filters)
        : [],
    });
    setTab(StatsTab.Reports);
  };

  const Content = useMemo(() => {
    if (loading) {
      return <Spinner className="mx-auto size-4" />;
    } else if (error) {
      return (
        <Body size="sm" weight="regular" className="text-center">
          There was an error loading this data.
        </Body>
      );
    } else if (data.length === 0) {
      return (
        <Body size="sm" weight="regular" className="text-center">
          There is nothing to see here.
        </Body>
      );
    } else {
      const headerText = getNameForExpenseReportAnalyticGroup(
        variables.groupedBy,
      );

      return (
        <>
          <div className="flex items-center justify-between">
            <Superscript size="md" className="text-[#676767]">
              {headerText}
            </Superscript>
            <Superscript size="md" className="text-[#676767]">
              AMOUNT
            </Superscript>
          </div>
          {data.map(({ groupBy, amount }) => (
            <div className="flex items-center space-x-4">
              {groupBy}
              <div className="flex-1" />
              {amount}
              {/* TODO: we need to get more data */}
              {/* <Avatar className="h-8 w-8">
                <AvatarFallback>
                  <Body size="sm" weight="regular">
                    NI
                  </Body>
                </AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <Body size="sm" weight="regular">
                  Nikolas Ioannou
                </Body>
                <Body size="xs" weight="regular" className="text-white/70">
                  nikolasgioannou+acme@gmail.com
                </Body>
              </div>
              <div className="flex-1" />
              <div className="space-y-1">
                <Body size="sm" weight="regular">
                  $1,234.56
                </Body>
                <Body size="xs" weight="regular" className="text-white/70">
                  15 Expenses
                </Body>
              </div> */}
            </div>
          ))}
        </>
      );
    }
  }, [data, error, loading, variables.groupedBy]);

  return (
    <div className={cn("flex flex-col space-y-4", className)}>
      <div className="mt-6 flex items-center justify-between">
        <Body size="lg" weight="regular">
          {headerText}
        </Body>
        <button
          className="flex h-8 w-8 items-center justify-center rounded transition-colors hover:bg-white/10"
          onClick={onClickOpenChart}
        >
          <ChartBar weight="bold" className="size-4" />
        </button>
      </div>
      <Separator className="my-4" />
      {Content}
    </div>
  );
};
